<template>
    <main class="p-2 md:p-5 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full">
            <div class="flex w-full sm:flex-row gap-2">
                <app-button
                    @click="addContractualDocument()"
                    :label="$t('contractualDocuments.newContractualDocument')"
                    v-if="!readOnly"
                />
            </div>
            <app-multi-picker
                ref="filter"
                icon="icon-magnify"
                :allowStringCriteria="true"
                class="w-full my-2"
                v-model="filterValue"
                :options="filterOptions"
                :strictMatching="true"
            >
                <template v-slot:option="{ option }">
                    <span>{{ option.name }}</span>
                    <span class="text-xs text-gray-600 ml-1">{{ option.criteriaType }}</span>
                </template>
            </app-multi-picker>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th style="width: 2rem" class="text-left border-r p-1 hidden sm:table-cell" v-if="!readOnly">
                            <app-checkbox
                                :value="selection.length === filteredItems.length && selection.length > 0"
                                :indeterminate="selection.length > 0 && selection.length < filteredItems.length"
                                :show-label="false"
                                :label="$t('commons.toggleSelectAll')"
                                @input="toggleSelectAll"
                            ></app-checkbox>
                        </th>
                        <th style="width: 2rem" class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('code')"
                            >
                                {{ $t('commons.code') }}
                                <div v-if="sortKey === 'code'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-left border-r p-1 bg-white">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('name')"
                            >
                                <span>{{ $t('commons.name') }}</span>
                                <div v-if="sortKey === 'name'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 10rem" class="text-left border-r p-1 hidden sm:table-cell">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emitter')"
                            >
                                {{ $t('bundles.emitter') }}
                                <div v-if="sortKey === 'emitter'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-center border-r p-1 hidden sm:table-cell" style="width: 6rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emissionDueDate')"
                            >
                                {{ $t('commons.expectedOn') }}
                                <div v-if="sortKey === 'emissionDueDate'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-center border-r p-1 hidden sm:table-cell" style="width: 6rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emissionDate')"
                            >
                                {{ $t('commons.emittedOn') }}
                                <div v-if="sortKey === 'emissionDate'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-center border-r p-1 hidden sm:table-cell" style="width: 6rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('signatureStatus')"
                            >
                                {{ $t('contractualDocuments.signatureStatus') }}
                                <div v-if="sortKey === 'emissionDate'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="contractualDocument in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="border-r p-1 hidden sm:table-cell" v-if="!readOnly">
                                <a :id="'uuid_' + contractualDocument.id" style="scroll-margin-top: 3em"></a>
                                <app-checkbox
                                    :value="contractualDocument.isSelected"
                                    :label="$t('commons.select')"
                                    :show-label="false"
                                    @input="saveSelection(contractualDocument)"
                                ></app-checkbox>
                            </td>
                            <td class="border-r p-1">
                                {{ contractualDocument.code }}
                            </td>
                            <td class="border-r p-1">
                                <div class="flex justify-between items-center">
                                    <router-link
                                        class="hover:underline"
                                        :to="{
                                            name: 'contractualDocument',
                                            params: {
                                                ...$route.params,
                                                contractualDocumentId: contractualDocument.id,
                                            },
                                        }"
                                    >
                                        {{ contractualDocument.name }}
                                        <span
                                            v-if="
                                                !contractualDocument.name ||
                                                contractualDocument.name.trim().length === 0
                                            "
                                        >
                                            ...
                                        </span>
                                    </router-link>
                                    <div class="flex" v-if="contractualDocument.emissionDate">
                                        <template v-for="attachment in contractualDocument.attachments">
                                            <app-file-link
                                                :url="attachment.url"
                                                :fileName="attachment.name"
                                                :showName="false"
                                            ></app-file-link>
                                        </template>
                                    </div>
                                </div>
                            </td>
                            <td class="border-r p-1 truncate hidden sm:table-cell">
                                <div v-if="contractualDocument.emitter" style="max-width: 10rem" class="truncate">
                                    <span class="">
                                        {{
                                            contractualDocument.emitter.reference
                                                ? '#' +
                                                  contractualDocument.emitter.reference +
                                                  ' ' +
                                                  contractualDocument.emitter.name
                                                : contractualDocument.emitter.name
                                        }}
                                    </span>
                                </div>
                            </td>
                            <td
                                class="text-center border-r p-1 hidden sm:table-cell hidden sm:table-cell"
                                :class="{
                                    'text-red-600 font-bold':
                                        !contractualDocument.emissionDate &&
                                        contractualDocument.emissionDueDate < new Date(),
                                }"
                            >
                                <app-date-link
                                    @enter="focusToNextEmissionDueDate(contractualDocument)"
                                    :ref="'emissionDueDate_' + contractualDocument.id"
                                    :disabled="readOnly"
                                    :label="$t('contractualDocuments.emissionDueDate')"
                                    :show-label="false"
                                    v-model="contractualDocument.emissionDueDate"
                                    @input="updateEmissionDueDate(contractualDocument, $event)"
                                ></app-date-link>
                            </td>
                            <td class="text-center border-r p-1 hidden sm:table-cell">
                                <app-date-link
                                    @enter="focusToNextDate(contractualDocument)"
                                    :ref="'date_' + contractualDocument.id"
                                    :disabled="readOnly"
                                    :default-date="contractualDocument.emissionDate"
                                    :label="$t('contractualDocuments.emissionDate')"
                                    :show-label="false"
                                    v-model="contractualDocument.emissionDate"
                                    @input="updateEmissionDate(contractualDocument, $event)"
                                ></app-date-link>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
        <app-popup ref="modifyAllPopup" :showHeader="true" :title="$t('commons.actionOnSelection')">
            <ValidationObserver
                v-slot="{ invalid, errors, dirty }"
                tag="form"
                ref="observer"
                class="p-2 gap-2 flex flex-col"
            >
                <app-bundle-picker v-model="editedContractualDocument.emitter" :options="bundles"></app-bundle-picker>

                <app-date-input
                    v-model="editedContractualDocument.emissionDueDate"
                    :label="$t('contractualDocuments.emissionDueDate')"
                ></app-date-input>
                <app-date-input
                    v-model="editedContractualDocument.emissionDate"
                    :label="$t('contractualDocuments.emissionDate')"
                />
                <app-footer @click="saveMultiple()" :disabled="invalid" class="mt-2"></app-footer>
            </ValidationObserver>
        </app-popup>
    </main>
</template>

<script>
import { filterMatch, sortBy } from '@/services/sanitize.service';
import AppSelect from '@/components/appSelect/AppSelect';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions';
import {
    createContractualDocument,
    getContractualDocuments,
    removeContractualDocument,
    updateContractualDocument,
} from './contractualDocuments.service';

import AppDateLink from '@/components/appDateLink/AppDateLink';
import { confirm } from '@/features/dialogs/dialogs.service';
import AppPopup from '@/components/app-popup/AppPopup';
import AppBundlePicker from '@/components/appBundlePicker';
import AppDateInput from '@/components/appDateInput/AppDateInput';
import AppFooter from '@/components/appFooter/AppFooter';
import AppCheckbox from '@/components/app-checkbox/AppCheckbox';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker';
import AppButton from '@/components/appButton/AppButton';
import IconFileDocumentOutline from '@/icons/IconFileDocumentOutline';
import AppFileLink from '@/components/appFileLink/AppFileLink';
import { combineLatest } from 'rxjs';
import { getBundleMap } from '@/features/bundles/bundles.service';
import { queryProject } from '@/features/projects/projects.service';
import AppTips from '@/components/app-tips/AppTips.vue';

export default {
    components: {
        AppTips,
        AppFileLink,
        IconFileDocumentOutline,
        AppButton,
        AppMultiPicker,
        AppFooter,
        AppDateInput,
        AppBundlePicker,
        AppPopup,
        AppQuickActions,
        AppDateLink,
        AppCheckbox,
        AppSelect,
    },
    async created() {
        this.restoreSelection();
        this.restoreFilter();
        queryProject(this.$route.params.projectId).then((project) => {
            this.readOnly = !project.me.allowedFeatures.includes('project_contractualDocuments');
        });
        this.init();
    },
    computed: {
        filteredItems() {
            let result = this.filterFn(this.filterValue).map((item) => ({
                ...item,
                isSelected: this.selection.includes(item.id),
            }));
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'code') {
                        return item.code;
                    } else if (this.sortKey === 'name') {
                        return item.name;
                    } else if (this.sortKey === 'emissionDueDate') {
                        return item.emissionDueDate;
                    } else if (this.sortKey === 'emissionDate') {
                        return item.emissionDate;
                    } else if (this.sortKey === 'emitter') {
                        return item.emitter ? item.emitter.reference + ' ' + item.emitter.name : '';
                    } else if (this.sortKey === 'signatureStatus') {
                        return item.signatureStatus;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
        filterOptions() {
            return [
                {
                    isGroup: true,
                    name: this.$t('commons.status'),
                    id: 'statusCriteriaType',
                    children: [
                        {
                            name: this.$t('project.follow.noEmitter'),
                            id: 'noEmitter',
                            criteriaType: '',
                            firstOfCriteriaType: false,
                            _isStatusTypeCriteriaType: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.bundleCriteriaType'),
                    id: this.$t('commons.bundleCriteriaType'),
                    children: this.bundles.map((bundle, index) => ({
                        ...bundle,
                        name: bundle.reference ? bundle.reference + ' - ' + bundle.name : +bundle.name,
                        firstOfCriteriaType: index === 0,
                        criteriaType: this.$t('commons.bundleCriteriaType'),
                        _isBundleCriteria: true,
                    })),
                },
            ];
        },
    },
    methods: {
        getSignatureStatus(contractualDocument) {
            return !contractualDocument.emissionDate || contractualDocument.signers.length === 0
                ? '-'
                : contractualDocument.signers.find((signer) => !signer.signedAt)
                ? 'pending'
                : contractualDocument.signers.every((signer) => signer.signedAt)
                ? 'signed'
                : 'error';
        },
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundleMap(this.$route.params.projectId),
                    getContractualDocuments(this.$route.params.projectId),
                ]).subscribe(([bundleMap, contractualDocuments]) => {
                    this.bundles = sortBy(Object.values(bundleMap), (bundle) => `${bundle.reference}${bundle.name}`);
                    this.items = contractualDocuments.map((contractualDocument) => {
                        return {
                            ...contractualDocument,
                            bundle: bundleMap[contractualDocument.bundleId],
                            signatureStatus: this.getSignatureStatus(contractualDocument),
                            filterString: [
                                contractualDocument.code,
                                contractualDocument.name,
                                contractualDocument.emitter
                                    ? contractualDocument.emitter.reference + '' + contractualDocument.emitter.name
                                    : '',
                            ].join(),
                            isSelected: this.selection.includes(contractualDocument.id),
                            emitter: bundleMap[contractualDocument.emitterId],
                        };
                    });
                    this.cleanupSavedSelection();
                    this.scrollToLastVisited();
                    this.loading = false;
                }),
            ];
        },
        scrollToLastVisited() {
            const lastVisitedContractualDocumentId = localStorage.getItem(
                'contractualDocument.lastVisitedContractualDocumentId.' + this.$route.params.projectId,
            );
            if (lastVisitedContractualDocumentId) {
                this.$nextTick(() => {
                    const element = this.$el.querySelector('#uuid_' + lastVisitedContractualDocumentId);
                    if (element) element.scrollIntoView();
                });
            }
        },
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
        },
        saveMultiple() {
            const selectedItems = this.items.filter((item) => this.selection.includes(item.id));
            const patch = {};
            if (
                this.editedContractualDocument.emissionDueDate &&
                this.editedContractualDocument.emissionDueDate !== this.getSelectionCommonEmissionDueDate(selectedItems)
            ) {
                patch.emissionDueDate = this.editedContractualDocument.emissionDueDate;
            }
            if (
                this.editedContractualDocument.emitter &&
                this.editedContractualDocument.emitter !== this.getSelectionCommonEmitter(selectedItems)
            ) {
                patch.emitterId = this.editedContractualDocument.emitter.id;
            }
            if (
                this.editedContractualDocument.emissionDate &&
                this.editedContractualDocument.emissionDate !== this.getSelectionCommonEmissionDate(selectedItems)
            ) {
                patch.emissionDate = this.editedContractualDocument.emissionDate;
            }
            this.$refs.modifyAllPopup.close();
        },
        async onAction(action) {
            const selectedItems = this.items.filter((item) => this.selection.includes(item.id));
            if (action === 'delete') {
                if (await confirm(this.$t('commons.confirmMessageAll', { number: this.selection.length }))) {
                    await Promise.all(
                        selectedItems.map((item) => removeContractualDocument(this.$route.params.projectId, item.id)),
                    );
                    this.selection = [];
                }
            } else if (action === 'modify') {
                this.editedContractualDocument = {
                    emissionDueDate: this.getSelectionCommonEmissionDueDate(selectedItems),
                    emissionDate: this.getSelectionCommonEmissionDate(selectedItems),
                    emitter: this.getSelectionCommonEmitter(selectedItems),
                };
                this.$refs.modifyAllPopup.open();
            }
        },
        getSelectionCommonEmitter(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.emitter);
            if (firstItem && selectedItems.every((item) => item.emitter && item.emitter.id === firstItem.emitter.id)) {
                return firstItem.emitter;
            } else {
                return null;
            }
        },
        getSelectionCommonEmissionDate(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.emissionDate);
            if (
                firstItem &&
                selectedItems.every(
                    (item) => item.emissionDate && item.emissionDate.getTime() === firstItem.emissionDate.getTime(),
                )
            ) {
                return firstItem.emissionDate;
            } else {
                return null;
            }
        },
        getSelectionCommonEmissionDueDate(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.emissionDueDate);
            if (
                firstItem &&
                selectedItems.every(
                    (item) =>
                        item.emissionDueDate && item.emissionDueDate.getTime() === firstItem.emissionDueDate.getTime(),
                )
            ) {
                return firstItem.emissionDueDate;
            } else {
                return null;
            }
        },
        toggleSelectAll() {
            if (this.selection.length < this.filteredItems.length) {
                this.selection = this.filteredItems.map((item) => item.id);
            } else {
                this.selection = [];
            }
            this.saveSelection();
        },
        cleanupSavedSelection() {
            this.selection = this.selection.filter((itemId) => !!this.items.find((anItem) => anItem.id === itemId));
        },
        saveSelection(item) {
            if (item) {
                if (!item.isSelected) {
                    this.selection.push(item.id);
                } else {
                    this.selection = this.selection.filter((anItem) => anItem !== item.id);
                }
            }
            localStorage.setItem(
                'contractualDocuments.selection.' + this.$route.params.projectId,
                JSON.stringify(this.selection),
            );
        },
        restoreSelection() {
            const cache = localStorage.getItem('contractualDocuments.selection.' + this.$route.params.projectId);
            if (cache) {
                this.selection = JSON.parse(cache);
            }
        },
        async addContractualDocument() {
            const result = await createContractualDocument(this.$route.params.projectId, {});
            await this.$router.push({
                name: 'contractualDocument',
                params: {
                    projectId: this.$route.params.projectId,
                    contractualDocumentId: result.id,
                },
            });
        },
        matchBundleFilter(item, bundleIds) {
            return bundleIds.includes(item.emitterId);
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString, criteria, true));
        },
        saveFilter(filterValue) {
            localStorage.setItem(
                'contractualDocuments_filter_' + this.$route.params.projectId,
                JSON.stringify({
                    filterValue,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('contractualDocuments_filter_' + this.$route.params.projectId);
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.filterValue = parsedCache.filterValue || [];
            }
        },
        filterFn(filter) {
            this.saveFilter(filter);
            const bundleIds = filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id);
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const noEmitterCriteria = filter.filter(
                (aCriteria) => aCriteria._isStatusTypeCriteriaType && aCriteria.id === 'noEmitter',
            );
            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                    matchNoEmitter: this.matchNoEmitter(noEmitterCriteria, item),
                };
                const filterResult = Object.values(fullCriteria).every((value) => !!value);
                if (!filterResult) {
                    this.selection = this.selection.filter((id) => id !== item.id);
                }
                return filterResult;
            });
        },
        matchNoEmitter(noEmitterCriteria, item) {
            return noEmitterCriteria.length === 0 || !item.emitter;
        },
        updateEmissionDueDate(contractualDocument, date) {
            updateContractualDocument(this.$route.params.projectId, {
                id: contractualDocument.id,
                emissionDueDate: date,
            });
        },
        updateEmissionDate(contractualDocument, date) {
            if (contractualDocument.emissionDueDate) {
                updateContractualDocument(this.$route.params.projectId, {
                    id: contractualDocument.id,
                    emissionDate: date,
                });
            } else {
                updateContractualDocument(this.$route.params.projectId, {
                    id: contractualDocument.id,
                    emissionDueDate: date,
                    emissionDate: date,
                });
            }
        },
        focusToNextDate(contractualDocument) {
            let index = this.filteredItems.findIndex(
                (aContractualDocument) => aContractualDocument.id === contractualDocument.id,
            );
            let nextContractualDocument = this.filteredItems[index + 1];
            while (!nextContractualDocument && index < this.filteredItems.length) {
                nextContractualDocument = this.filteredItems[++index];
            }
            if (nextContractualDocument) {
                this.$emit('select', nextContractualDocument);
                const element = this.$refs['date_' + nextContractualDocument.id];
                if (element) {
                    if (Array.isArray(element)) {
                        element[0].focus();
                    } else {
                        element.focus();
                    }
                }
            }
        },
        focusToNextEmissionDueDate(contractualDocument) {
            let index = this.filteredItems.findIndex(
                (aContractualDocument) => aContractualDocument.id === contractualDocument.id,
            );
            let nextContractualDocument = this.filteredItems[index + 1];
            while (!nextContractualDocument && index < this.filteredItems.length) {
                nextContractualDocument = this.filteredItems[++index];
            }
            if (nextContractualDocument) {
                this.$emit('select', nextContractualDocument);
                const element = this.$refs['emissionDueDate_' + nextContractualDocument.id];
                if (element) {
                    if (Array.isArray(element)) {
                        element[0].focus();
                    } else {
                        element.focus();
                    }
                }
            }
        },
    },
    data() {
        return {
            loading: true,
            quickActions: [
                {
                    name: this.$t('contractualDocuments.newContractualDocument'),
                    run: () => this.addContractualDocument(),
                },
            ],
            sortKey: 'name',
            sortAsc: true,
            readOnly: true,
            selection: [],
            editedContractualDocument: {
                emissionDueDate: null,
                emissionDate: null,
                type: null,
                locations: [],
                service: null,
                emitter: null,
            },
            items: [],
            bundles: [],
            filterValue: [],
        };
    },
};
</script>
